import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getVehiclesFromDb } from "../../api/getVehiclesFromDb";
import { getAssetErrors } from "../../api/getErrors";
import { userHasAccess } from "../../services/RoleBasedAccess";
import { State } from '../../state/reducers';
import { Account, Asset } from "../../state/types";
import uploadAndProcessImages from "../../components/vehicles/FileUploading/uploadRequests";
import { UploadState } from "./useUploadAsset";
import { useSession } from "../../context/SessionContext";

export interface ImageCategory {
  key: string;
  label: string;
  images: string[];
  visible?: boolean;
}

function generateImageCategories(asset: Asset | null): ImageCategory[] {
  if (!asset) return []

  const hasBgrPano = !!asset.imageBgrPano?.length;
  const panoImages = hasBgrPano ? asset.imageBgrPano : asset.imagePano;
  const panoLabel = hasBgrPano ? "Panoramic (BGR)" : "Panoramic";

  const hasDewarp = !!asset.imageUndercarriageDewarp?.length;
  const ucFallback = asset.imageUndercarriage || [];

  return [
    { key: "exterior", label: "Exterior", images: asset.imageExterior || [] },
    { key: "crop", label: "Cropped", images: asset.imageCrop || [] },
    { key: "featured", label: "Featured", images: asset.imageFeatured || [] },
    { key: "bgr", label: "Background Replaced", images: asset.imageBgr || [] },
    { key: "manual", label: "Manual", images: asset.imageManual || [] },
    { key: "pano", label: panoLabel, images: panoImages || [] },
    {
      key: "uc",
      label: hasDewarp ? "Undercarriage" : "Raw Undercarriage",
      images: hasDewarp ? asset.imageUndercarriageDewarp : ucFallback,
    },
  ];
}


export const useAssetState = () => {
  const navigate = useNavigate();
  const { assetId, clientId } = useParams<{ assetId: string; clientId: string }>();
  const { user, setIsLoading } = useSession();
  const userLoaded = !!user && !!user.role;

  const [asset, setAsset] = useState<Asset | null>(null);
  const [errors, setErrors] = useState<any[]>([]);
  const [visibleTypes, setVisibleTypes] = useState<{ [key: string]: boolean }>({});
  const [isEditAssetIdModalOpen, setIsEditAssetIdModalOpen] = useState(false);
  const [isEditAssetModalOpen, setIsEditAssetModalOpen] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Data authorization and fetching
  useEffect(() => {
    if (!userLoaded || !user) return;

    if (!clientId || !assetId) {
      navigate("/");
      return;
    }

    const baseClientId = clientId.replace(/U\d{2}$/, '');
    const isInternalUser = userHasAccess(user.role, ["Internal"]);
    if (!isInternalUser && !user?.system_access.includes(baseClientId)) {
      navigate("/");
    }

    const getVehicleData = async () => {
      setIsLoading(true);
      try {
        const body = {
          page: 1,
          per_page: 1,
          system_code: clientId,
          asset_id: assetId,
          archived: true
        };

        const { vehicles: assets } = await getVehiclesFromDb(body);
        const fetchedAsset = assets[0];
        setAsset(fetchedAsset);

        const errorResponse = await getAssetErrors(clientId, assetId.split("-")[0]);
        setErrors(errorResponse.errors || []);

        // Set initial visibility of image types
        if (fetchedAsset) {
          setVisibleTypes({
            exterior: !!fetchedAsset.imageExterior?.length,
            crop: !!fetchedAsset.imageCrop?.length,
            featured: !!fetchedAsset.imageFeatured?.length,
            manual: !!fetchedAsset.imageManual?.length,
            pano: !!fetchedAsset.imageBgrPano?.length || !!fetchedAsset.imagePano?.length,
            uc: !!fetchedAsset.imageUndercarriageDewarp?.length || !!fetchedAsset.imageUndercarriage?.length,
            bgr: !!fetchedAsset.imageBgr?.length,
          });
        }
      } catch (error) {
        console.error("Error fetching vehicle data:", error);
        setAsset(null);
      } finally {
        setIsLoading(false);
      }
    };

    getVehicleData();
  }, [user, userLoaded, clientId, assetId, navigate, refetchTrigger]);

  // --- Document Title Effect (no change) ---
  useEffect(() => {
    const originalTitle = document.title;
    document.title = assetId ? assetId.split("-")[0] : "Black Widow UMS";
    return () => { document.title = originalTitle; };
  }, [assetId]);

  const imageCategories = useMemo(() => generateImageCategories(asset), [asset]);

  // --- Callbacks (no change) ---
  const updateAssetMedia = useCallback(async (state: UploadState) => {
    if (!asset) {
      console.error("Cannot update a non-existent asset.");
      return;
    }
    const { asset: updatedAssetState, uploadedFiles } = state;
    const modifiedImageArrays: Record<string, any[]> = {};

    Object.keys(updatedAssetState).forEach(key => {
      if (key.startsWith('image') && Array.isArray(updatedAssetState[key as keyof Asset])) {
        if (updatedAssetState[key as keyof Asset] !== asset[key as keyof Asset]) {
          modifiedImageArrays[key] = updatedAssetState[key as keyof Asset] as any[];
        }
      }
    });

    try {
      await uploadAndProcessImages(asset, uploadedFiles, modifiedImageArrays);
    } catch (error) {
      console.error("Failed to update asset:", error);
    }
  }, [asset]);

  const refetchAsset = useCallback(() => {
    setRefetchTrigger(prev => prev + 1);
  }, []);

  return {
    asset,
    assetId,
    clientId,
    errors,
    visibleTypes,
    setVisibleTypes,
    imageCategories,
    updateAssetMedia,
    refetchAsset,

    isEditAssetIdModalOpen,
    openEditAssetIdModal: () => setIsEditAssetIdModalOpen(true),
    closeEditAssetIdModal: () => setIsEditAssetIdModalOpen(false),

    isEditAssetModalOpen,
    openEditAssetModal: () => setIsEditAssetModalOpen(true),
    closeEditAssetModal: () => setIsEditAssetModalOpen(false),
  };
};